export interface SliderElements {
  sectionEl: HTMLElement;
  scroller: HTMLElement;
  leftBtn: HTMLButtonElement | null;
  rightBtn: HTMLButtonElement | null;
  leftFade: HTMLElement | null;
  rightFade: HTMLElement | null;
  items: HTMLElement[];
}

export function getSliderElements(sectionEl: HTMLElement, sectionId: string): SliderElements | null {
  const scroller = sectionEl.querySelector<HTMLElement>(`#recs-scroll-${sectionId}`);
  if (!scroller) return null;

  const leftBtn = sectionEl.querySelector<HTMLButtonElement>(`#recs-arrow-left-${sectionId}`);
  const rightBtn = sectionEl.querySelector<HTMLButtonElement>(`#recs-arrow-right-${sectionId}`);
  const leftFade = sectionEl.querySelector<HTMLElement>(`#left-scroll-container-${sectionId}`);
  const rightFade = sectionEl.querySelector<HTMLElement>(`#right-scroll-container-${sectionId}`);
  const items = Array.from(sectionEl.querySelectorAll<HTMLElement>(`[data-current-index]`));

  return { sectionEl, scroller, leftBtn, rightBtn, leftFade, rightFade, items };
}

export function isFullyVisible(el: HTMLElement, containerRect: DOMRect, tolerancePx = 4): boolean {
  const rect = el.getBoundingClientRect();
  return rect.left >= containerRect.left - tolerancePx && rect.right <= containerRect.right + tolerancePx;
}

const setSliderEdgeVisible = (
  visible: boolean,
  btn: HTMLButtonElement | null,
  fade: HTMLElement | null,
  mdOnly: boolean,
  isMdUp: boolean,
): void => {
  const show = visible && (!mdOnly || isMdUp);

  if (fade) {
    fade.classList.toggle('hidden', !show);
    return;
  }

  if (btn) btn.style.display = show ? 'flex' : 'none';
};

export function updateArrowVisibility(elements: SliderElements): void {
  const { scroller, leftBtn, rightBtn, leftFade, rightFade, items } = elements;

  if (!items.length) {
    setSliderEdgeVisible(false, leftBtn, leftFade, true, false);
    setSliderEdgeVisible(false, rightBtn, rightFade, true, false);
    return;
  }

  const canScroll = scroller.scrollWidth - scroller.clientWidth > 1;
  const isMdUp = window.matchMedia?.('(min-width: 768px)').matches ?? (window.innerWidth >= 768);

  if (!canScroll) {
    setSliderEdgeVisible(false, leftBtn, leftFade, true, isMdUp);
    setSliderEdgeVisible(false, rightBtn, rightFade, true, isMdUp);
    return;
  }

  const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
  const usesFadeEdges = Boolean(leftFade || rightFade);

  let atStart: boolean;
  let atEnd: boolean;

  if (usesFadeEdges) {
    atStart = scroller.scrollLeft <= 10;
    atEnd = scroller.scrollLeft >= maxScrollLeft - 10;
  } else {
    const sRect = scroller.getBoundingClientRect();
    const firstEl = items[0];
    const lastEl = items[items.length - 1];
    atStart = scroller.scrollLeft <= 1 || isFullyVisible(firstEl, sRect);
    atEnd = isFullyVisible(lastEl, sRect) || scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;
  }

  setSliderEdgeVisible(!atStart, leftBtn, leftFade, true, isMdUp);
  setSliderEdgeVisible(!atEnd, rightBtn, rightFade, true, isMdUp);
}

export function enableSwatchesWhenNotScrollable(scroller: HTMLElement, sectionEl: HTMLElement): void {
  const canScroll = scroller.scrollWidth - scroller.clientWidth > 1;
  if (canScroll) return;

  const productCards = sectionEl.querySelectorAll<HTMLElement>('.product-card[data-swatches-lock-initial="true"]');
  productCards.forEach((card) => {
    const swatchSwiperEl = card.querySelector<HTMLElement>('.swatch-swiper');
    if (swatchSwiperEl && (swatchSwiperEl as any).swiper) {
      try {
        (swatchSwiperEl as any).swiper.allowTouchMove = true;
        (swatchSwiperEl as any).swiper.update();
      } catch {}
    }
  });
}

export function initDragToScroll(scroller: HTMLElement, elements: SliderElements, _sectionId: string): void {
  let isPointerDown = false;
  let isDragging = false;
  let activePointerId = -1;
  let startX = 0;
  let scrollLeftStart = 0;
  let dragDistance = 0;
  let suppressNextClick = false;
  const originalScrollSnap = getComputedStyle(scroller).scrollSnapType;

  scroller.style.cursor = "grab";

  const endDrag = (pointerId: number): void => {
    if (!isPointerDown && !isDragging) return;
    isPointerDown = false;
    isDragging = false;
    if (scroller.hasPointerCapture(pointerId)) {
      scroller.releasePointerCapture(pointerId);
    }
    scroller.style.cursor = "grab";
    window.requestAnimationFrame(() => {
      scroller.style.scrollSnapType = originalScrollSnap;
    });
    if (dragDistance > 5) suppressNextClick = true;
    activePointerId = -1;
  };

  scroller.addEventListener(
    "pointerdown",
    (event: PointerEvent) => {
      if (event.pointerType !== "mouse") return;
      if (event.button !== 0) return;

      isPointerDown = true;
      isDragging = false;
      dragDistance = 0;
      activePointerId = event.pointerId;
      startX = event.clientX;
      scrollLeftStart = scroller.scrollLeft;
    },
    { capture: true },
  );

  scroller.addEventListener(
    "pointermove",
    (event: PointerEvent) => {
      if (!isPointerDown || event.pointerId !== activePointerId) return;

      const deltaX = event.clientX - startX;
      dragDistance = Math.max(dragDistance, Math.abs(deltaX));
      if (dragDistance <= 3) return;

      if (!isDragging) {
        isDragging = true;
        scroller.setPointerCapture(event.pointerId);
        scroller.style.cursor = "grabbing";
        scroller.style.scrollSnapType = "none";
      }

      event.preventDefault();
      scroller.scrollLeft = scrollLeftStart - deltaX;
      updateArrowVisibility(elements);
    },
    { capture: true },
  );

  scroller.addEventListener(
    "pointerup",
    (event: PointerEvent) => {
      endDrag(event.pointerId);
    },
    { capture: true },
  );

  scroller.addEventListener(
    "pointercancel",
    (event: PointerEvent) => {
      endDrag(event.pointerId);
    },
    { capture: true },
  );

  scroller.addEventListener(
    "click",
    (event: MouseEvent) => {
      if (!suppressNextClick) return;
      event.preventDefault();
      event.stopPropagation();
      suppressNextClick = false;
    },
    { capture: true },
  );
}

export function updateProductCardTouchHandling(sectionEl: HTMLElement): void {
  const productCards = sectionEl.querySelectorAll<HTMLElement>('.product-card');
  productCards.forEach((card) => {
    const imageSwiperEl = card.querySelector<HTMLElement>('.product-image-swiper');
    if (imageSwiperEl) {
      if ((imageSwiperEl as any).swiper && (imageSwiperEl as any).swiper.allowTouchMove === false) {
        imageSwiperEl.style.touchAction = 'pan-x pan-y';
        imageSwiperEl.style.pointerEvents = 'auto';
      }
      const images = imageSwiperEl.querySelectorAll<HTMLImageElement>('img');
      images.forEach((img) => {
        img.style.touchAction = 'pan-x pan-y';
      });
    }
  });
}

export interface SliderBehaviorOptions {
  enableDrag?: boolean;
}

export function attachSliderBehavior(
  elements: SliderElements,
  sectionId: string,
  onAfterInit?: () => void,
  options: SliderBehaviorOptions = {},
): void {
  const { scroller, leftBtn, rightBtn, leftFade, rightFade } = elements;
  const isMegaMenuSlider = Boolean(elements.sectionEl.closest("[data-mega-menu]"));

  const getStep = () => Math.max(scroller.clientWidth * 0.8, 200);

  const scrollLeft = () => {
    scroller.scrollBy({ left: -getStep(), behavior: "smooth" });
    setTimeout(() => updateArrowVisibility(elements), 350);
  };

  const scrollRight = () => {
    scroller.scrollBy({ left: getStep(), behavior: "smooth" });
    setTimeout(() => updateArrowVisibility(elements), 350);
  };

  const keepMegaMenuOpenOnPointerDown = (event: MouseEvent) => {
    if (!isMegaMenuSlider) return;
    event.preventDefault();
    event.stopPropagation();
  };

  leftBtn?.addEventListener("mousedown", keepMegaMenuOpenOnPointerDown);
  rightBtn?.addEventListener("mousedown", keepMegaMenuOpenOnPointerDown);

  leftBtn?.addEventListener("click", (event) => {
    if (isMegaMenuSlider) event.stopPropagation();
    scrollLeft();
  });

  rightBtn?.addEventListener("click", (event) => {
    if (isMegaMenuSlider) event.stopPropagation();
    scrollRight();
  });

  leftFade?.addEventListener("mousedown", keepMegaMenuOpenOnPointerDown);
  rightFade?.addEventListener("mousedown", keepMegaMenuOpenOnPointerDown);

  leftFade?.addEventListener("click", (event) => {
    if (!isMegaMenuSlider) return;
    event.stopPropagation();
    scrollLeft();
  });

  rightFade?.addEventListener("click", (event) => {
    if (!isMegaMenuSlider) return;
    event.stopPropagation();
    scrollRight();
  });

  scroller.addEventListener('scroll', () => updateArrowVisibility(elements), { passive: true });
  scroller.addEventListener?.('scrollend', () => updateArrowVisibility(elements));

  const ro = new ResizeObserver(() => {
    updateArrowVisibility(elements);
    setTimeout(() => enableSwatchesWhenNotScrollable(scroller, elements.sectionEl), 100);
  });
  ro.observe(scroller);
  if (scroller.firstElementChild) ro.observe(scroller.firstElementChild);

  window.addEventListener('resize', () => {
    updateArrowVisibility(elements);
    setTimeout(() => enableSwatchesWhenNotScrollable(scroller, elements.sectionEl), 100);
  });

  if (options.enableDrag !== false) {
    initDragToScroll(scroller, elements, sectionId);
  }

  setTimeout(() => {
    updateArrowVisibility(elements);
    updateProductCardTouchHandling(elements.sectionEl);
    setTimeout(() => enableSwatchesWhenNotScrollable(scroller, elements.sectionEl), 200);
    onAfterInit?.();
  }, 0);

  setTimeout(() => {
    const observer = new MutationObserver(() => {
      updateProductCardTouchHandling(elements.sectionEl);
    });
    observer.observe(elements.sectionEl, { childList: true, subtree: true });
  }, 300);
}
